/**
 * Module-level poller for the platform maintenance status. One interval per page however many
 * components subscribe; works in the main app (SSR + hydration) and in the embed widget alike,
 * because it needs no router and no query client.
 *
 * The answer is classified into three states:
 *   none                 nothing to show (also the answer to every error, deliberately: a broken
 *                        maintenance service must not paint every tenant with a notice)
 *   notice               the maintenance service returned an active notice
 *   gateway-maintenance  HAProxy answered 503 with X-Platform-Maintenance: 1, the cluster is down
 *
 * While in gateway maintenance the poll interval drops to 15 s, and the first answer that is not
 * the gateway page clears the state again. Nothing reloads: the SignalR reconnect and the user's
 * next navigation pick the app back up, whereas a reload of every open page at the same moment
 * would land on a cluster that has only just come back.
 */

export type PlatformStatusScope = 'all' | 'live' | 'on-demand';
export type PlatformStatusSeverity = 'info' | 'warning';

export interface PlatformNotice {
    scope: PlatformStatusScope;
    severity: PlatformStatusSeverity;
    message: Record<string, string>;
    showFrom?: string;
    showUntil?: string;
}

export type PlatformStatus = { kind: 'none' } | { kind: 'notice'; notice: PlatformNotice } | { kind: 'gateway-maintenance' };

export const POLL_INTERVAL_MS = 60_000;
export const MAINTENANCE_POLL_INTERVAL_MS = 15_000;
// A connection that is accepted but never answered is exactly what a degrading cluster produces,
// and it is the one thing that could end the loop: without a deadline the poll — and with it the
// recovery — would wait forever.
export const FETCH_TIMEOUT_MS = 10_000;
export const MAINTENANCE_HEADER = 'x-platform-maintenance';

const NONE: PlatformStatus = { kind: 'none' };
const GATEWAY_MAINTENANCE: PlatformStatus = { kind: 'gateway-maintenance' };

interface PlatformStatusOptions {
    baseUrl: string;
}

let options: PlatformStatusOptions = { baseUrl: '' };

let current: PlatformStatus = NONE;
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | undefined;

/** The embed widget runs on a customer's page and must poll the tenant host, not its own origin. */
export const configurePlatformStatus = (overrides: Partial<PlatformStatusOptions>) => {
    options = { ...options, ...overrides };
};

const isScope = (value: unknown): value is PlatformStatusScope => value === 'all' || value === 'live' || value === 'on-demand';
const isSeverity = (value: unknown): value is PlatformStatusSeverity => value === 'info' || value === 'warning';

export const classifyResponse = async (response: Response): Promise<PlatformStatus> => {
    if (response.status === 503 && response.headers.get(MAINTENANCE_HEADER) === '1') {
        return GATEWAY_MAINTENANCE;
    }
    if (!response.ok) {
        return NONE;
    }
    let body: any;
    try {
        body = await response.json();
    } catch {
        return NONE;
    }
    // typeof [] === 'object', so the message map is checked for being an array too: the banner
    // looks its text up by language key and an array carries none.
    if (
        !body ||
        body.active !== true ||
        !isScope(body.scope) ||
        !isSeverity(body.severity) ||
        typeof body.message !== 'object' ||
        body.message === null ||
        Array.isArray(body.message)
    ) {
        return NONE;
    }
    return {
        kind: 'notice',
        notice: {
            scope: body.scope,
            severity: body.severity,
            message: body.message,
            showFrom: typeof body.showFrom === 'string' ? body.showFrom : undefined,
            showUntil: typeof body.showUntil === 'string' ? body.showUntil : undefined
        }
    };
};

const setCurrent = (next: PlatformStatus) => {
    // Same content means same reference: useSyncExternalStore re-renders on identity, and a
    // 60-second poll must not re-render every subscriber when nothing changed.
    if (JSON.stringify(next) === JSON.stringify(current)) {
        return;
    }
    current = next;
    listeners.forEach((listener) => listener());
};

export const pollPlatformStatus = async (): Promise<PlatformStatus> => {
    try {
        // credentials: 'omit' — the embed polls the tenant host from the customer's page, so this is
        // a cross-origin request; the service never reads the AuthSession cookie, and the wildcard
        // Access-Control-Allow-Origin it answers with is only valid for an uncredentialed request.
        const response = await fetch(`${options.baseUrl}/platform-status`, {
            cache: 'no-store',
            credentials: 'omit',
            headers: { Accept: 'application/json' },
            signal: AbortSignal.timeout(FETCH_TIMEOUT_MS)
        });
        setCurrent(await classifyResponse(response));
    } catch {
        setCurrent(NONE);
    }
    return current;
};

const schedule = () => {
    clearTimeout(timer);
    const delay = current.kind === 'gateway-maintenance' ? MAINTENANCE_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
    timer = setTimeout(async () => {
        try {
            await pollPlatformStatus();
        } finally {
            // In a finally so no path through the poll can end the loop; the guard keeps an
            // unsubscribed poller from arming a timer nobody asked for any more.
            if (listeners.size > 0) {
                schedule();
            }
        }
    }, delay);
};

export const subscribePlatformStatus = (listener: () => void) => {
    listeners.add(listener);
    if (listeners.size === 1) {
        void pollPlatformStatus().then(() => {
            if (listeners.size > 0) {
                schedule();
            }
        });
    }
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
            clearTimeout(timer);
            timer = undefined;
        }
    };
};

export const getPlatformStatus = (): PlatformStatus => current;

/** SSR renders no notice; the client polls after hydration, so server and client markup match. */
export const getServerPlatformStatus = (): PlatformStatus => NONE;

/** Tests only: back to the initial state between cases. */
export const resetPlatformStatus = () => {
    clearTimeout(timer);
    timer = undefined;
    listeners.clear();
    current = NONE;
    options = { baseUrl: '' };
};
